import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import { CONTACT_US_FORM_FIELDS } from "@thkStore/Constants/ContactUs/formFields";
import { TELEPHONE_COUNTRY_CODES } from "@thkStore/Shared/Constants/TelephoneCountryCodeConstants";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.isRequired]: boolean;
    };
}

const ALLOWED_COUNTRY_ABBREVIATIONS = Object.keys(TELEPHONE_COUNTRY_CODES);

const mapStateToProps = (state: ApplicationState) => ({
    country: state.components.contactUsForm.fieldValues[CONTACT_US_FORM_FIELDS.country],
    countries: getCurrentCountries(state),
});

const mapDispatchToProps = { setFieldValue, loadCurrentCountries };

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CountryStyles {
    countrySelect?: SelectPresentationProps;
}

export const countryStyles: CountryStyles = {};

const styles = countryStyles;

const Country: React.FC<Props> = ({ fields, country, countries, setFieldValue, loadCurrentCountries }) => {
    const { label, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [errorMessage, setErrorMessage] = React.useState("");

    React.useEffect(() => {
        if (!countries) {
            loadCurrentCountries();
        }
    }, [countries]);

    React.useEffect(() => {
        contactFormContext.validators.country = validate;
        if (typeof country !== "undefined") validate();
    }, [country, isRequired]);

    const validate = () => {
        const error = isRequired && !country
            ? (siteMessage("Field_Required", label) as string)
            : "";
        setErrorMessage(error);
        return !error;
    };

    const allowedCountries = (countries ?? []).filter(c =>
        ALLOWED_COUNTRY_ABBREVIATIONS.includes(c.abbreviation),
    );

    const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setFieldValue({ key: CONTACT_US_FORM_FIELDS.country, value: event.target.value });
        // Reset the dependent State whenever the Country changes.
        setFieldValue({ key: CONTACT_US_FORM_FIELDS.state, value: "" });
    };

    return (
        <Select
            {...styles.countrySelect}
            label={label}
            required={isRequired}
            value={country || ""}
            error={errorMessage}
            onChange={handleChange}
        >
            <option value="">{translate("Select Country")}</option>
            {allowedCountries.map(c => (
                <option key={c.abbreviation} value={c.abbreviation}>
                    {c.name}
                </option>
            ))}
        </Select>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(Country),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            { name: fields.label, displayName: "Label", editorTemplate: "TextField", fieldType: "Translatable", defaultValue: "Country", isRequired: true },
            { name: fields.isRequired, displayName: "Required Field", editorTemplate: "CheckboxField", defaultValue: true, fieldType: "General" },
        ],
    },
};

export default widgetModule;
